import React, { useState, useEffect } from 'react';
import PageHeader from './components/PageHeader';
import { apiFetch } from './utils/api';

const STATUS_COLORS = {
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-800', dot: '🔵' },
  generating: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: '🟡' },
  awaiting_approval: { bg: 'bg-orange-100', text: 'text-orange-800', dot: '🟠' },
  approved: { bg: 'bg-green-100', text: 'text-green-800', dot: '✅' },
  completed: { bg: 'bg-green-100', text: 'text-green-800', dot: '🟢' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800', dot: '❌' },
  failed: { bg: 'bg-red-100', text: 'text-red-800', dot: '🔴' },
  sent: { bg: 'bg-purple-100', text: 'text-purple-800', dot: '📤' },
};

const SCHEDULE_ICONS = {
  one_off: '📌', daily: '📅', weekly: '🔄', monthly: '🗓️', quarterly: '📊',
};

const TYPE_ICONS = {
  newsletter: '📰',
  blog: '📝',
};

const TYPE_LABELS = {
  newsletter: 'Newsletter',
  blog: 'Blog Article',
};

const CHANNEL_LABELS = {
  email: '✉️ Email',
  wordpress: '🌐 WordPress',
};

const STATUS_FLOW = {
  scheduled: 'Hermes will generate content when due',
  generating: 'Hermes is generating your newsletter',
  awaiting_approval: 'Review the newsletter — approve or reject',
  approved: 'Queued for sending',
  rejected: 'Rejected — needs rework',
  completed: 'Done',
  failed: 'Generation failed',
  sent: 'Sent to contacts',
};

function campaignPreviewHtml(campaign) {
  if (!campaign) return '';
  if (campaign.approved_html_content) return campaign.approved_html_content;
  return campaign.generated_html_content || '';
}

function campaignTemplateLabel(campaign) {
  if (!campaign) return '';
  if (campaign.master_template_key) {
    return campaign.master_template_key === 'domestic'
      ? 'ADG Home Gate & Security News'
      : 'ADG Commercial Access Update';
  }
  return '';
}

function canGenerateCampaign(campaign) {
  return Boolean(
    campaign &&
    campaign.status === 'scheduled' &&
    !campaign.generated_html_content &&
    campaign.master_template_key
  );
}

function canDeleteCampaign(campaign) {
  return campaign && !['approved', 'sent'].includes(campaign.status);
}

function CampaignPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [approvedTopics, setApprovedTopics] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // New campaign form state
  const [newCampaign, setNewCampaign] = useState({
    name: '', type: 'newsletter', delivery_channel: 'email', topic_id: '', schedule_type: 'weekly', due_at: '', variable_slots: '',
  });

  useEffect(() => { loadCampaigns(); loadApprovedTopics(); }, []);

  const loadCampaigns = async () => {
    try {
      const res = await apiFetch('/api/marketing/campaigns');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setLoadError(`Error ${res.status}: ${err.error || 'Unknown'}`);
        return;
      }
      const data = await res.json();
      setCampaigns(data);
    } catch (err) {
      setLoadError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const loadApprovedTopics = async () => {
    try {
      const res = await apiFetch('/api/marketing/topics?status=approved&limit=50');
      if (res.ok) {
        const data = await res.json();
        setApprovedTopics(data.topics || []);
      }
    } catch (err) { /* non-critical */ }
  };

  const loadDetail = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetailError(null);
    setSelectedDetail(null);
    setShowPreview(false);
    try {
      const res = await apiFetch(`/api/marketing/campaigns/${id}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setDetailError(`Error ${res.status}: ${err.error || 'Unknown'}`);
        return;
      }
      const data = await res.json();
      setSelectedDetail(data);
      // Fetch analytics for this campaign
      try {
        const aRes = await apiFetch(`/api/marketing/analytics/${id}`);
        if (aRes.ok) setAnalytics(await aRes.json());
        else setAnalytics(null);
      } catch { setAnalytics(null); }
    } catch (err) {
      setDetailError(err.message || 'Failed to load campaign');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAction = async (action, id, body) => {
    setActionLoading(action);
    try {
      const res = await apiFetch(`/api/marketing/campaigns/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`${action} failed: ${err.error || 'Unknown error'}`);
        return;
      }
      // Reload detail and list
      await loadDetail(id);
      await loadCampaigns();
    } catch (err) {
      alert(`${action} failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteCampaign = async (campaign) => {
    if (!canDeleteCampaign(campaign)) {
      alert('Approved or sent campaigns cannot be deleted.');
      return;
    }

    const confirmed = window.confirm(`Delete campaign "${campaign.name}"? This cannot be undone.`);
    if (!confirmed) return;

    setActionLoading('delete');
    try {
      const res = await apiFetch(`/api/marketing/campaigns/${campaign.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Delete failed: ${err.error || 'Unknown error'}`);
        return;
      }
      setSelectedId(null);
      setSelectedDetail(null);
      setShowPreview(false);
      await loadCampaigns();
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const createCampaign = async (e) => {
    e.preventDefault();
    if (!newCampaign.name || !newCampaign.due_at) {
      alert('Name and due date are required');
      return;
    }
    try {
      const payload = {
        name: newCampaign.name,
        type: newCampaign.type,
        delivery_channel: newCampaign.type === 'blog' ? 'wordpress' : 'email',
        schedule_type: newCampaign.schedule_type,
        due_at: new Date(newCampaign.due_at).toISOString(),
        variable_slots: newCampaign.variable_slots
          ? JSON.parse(newCampaign.variable_slots)
          : {},
      };
      const res = await apiFetch('/api/marketing/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Create failed: ${err.error || 'Unknown'}`);
        return;
      }
      setShowNewCampaign(false);
      setNewCampaign({ name: '', type: 'newsletter', delivery_channel: 'email', topic_id: '', schedule_type: 'weekly', due_at: '', variable_slots: '' });
      loadCampaigns();
    } catch (err) {
      alert(`Create failed: ${err.message}`);
    }
  };

  const filtered = campaigns
    .filter(c => filter === 'all' || c.status === filter)
    .filter(c => typeFilter === 'all' || c.type === typeFilter);
  const stats = {};
  campaigns.forEach(c => { stats[c.status] = (stats[c.status] || 0) + 1; });

  // Highlight awaiting_approval count
  const awaitingCount = stats.awaiting_approval || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Campaigns"
        subtitle="Manage email campaigns and newsletters"
        backTo="/marketing-hub"
        backLabel="Marketing Hub"
        sticky
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loadError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-700 font-medium">⚠️ {loadError}</p>
          </div>
        )}

        {/* Awaiting approval alert */}
        {awaitingCount > 0 && (
          <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">🟠</span>
              <p className="text-sm text-orange-800 font-medium">{awaitingCount} campaign{awaitingCount > 1 ? 's' : ''} awaiting your approval</p>
            </div>
            <button onClick={() => setFilter('awaiting_approval')} className="text-xs px-3 py-1.5 bg-orange-600 text-white rounded font-medium hover:bg-orange-700">
              Review Now
            </button>
          </div>
        )}

        {/* Top bar: type filters + status filters + new campaign button */}
        <div className="space-y-3 mb-6">
          {/* Type filter */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-2 flex-wrap">
              {[
                { key: 'all', label: 'All Types', count: campaigns.length },
                { key: 'newsletter', label: '📰 Newsletters', count: campaigns.filter(c => c.type === 'newsletter').length },
                { key: 'blog', label: '📝 Blog', count: campaigns.filter(c => c.type === 'blog').length },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setTypeFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    typeFilter === f.key
                      ? 'bg-gray-800 text-white border-gray-800'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'
                  }`}
                >
                  {f.label} ({f.count})
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowNewCampaign(!showNewCampaign)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              + New Campaign
            </button>
          </div>

          {/* Status filter */}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'all', label: 'All', count: campaigns.length },
              { key: 'awaiting_approval', label: 'Awaiting', count: awaitingCount },
              { key: 'approved', label: 'Approved', count: stats.approved || 0 },
              { key: 'scheduled', label: 'Scheduled', count: stats.scheduled || 0 },
              { key: 'generating', label: 'Generating', count: stats.generating || 0 },
              { key: 'sent', label: 'Sent', count: stats.sent || 0 },
              { key: 'failed', label: 'Failed', count: stats.failed || 0 },
              { key: 'rejected', label: 'Rejected', count: stats.rejected || 0 },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  filter === f.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300'
                }`}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>
        </div>

        {/* New campaign form */}
        {showNewCampaign && (
          <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">New Campaign</h3>
            <form onSubmit={createCampaign} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={e => setNewCampaign({...newCampaign, name: e.target.value})}
                  placeholder="e.g. ADG Commercial Weekly #8"
                  className="w-full px-3 py-2 border rounded text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select
                  value={newCampaign.type}
                  onChange={e => setNewCampaign({...newCampaign, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="newsletter">📰 Newsletter</option>
                  <option value="blog">📝 Blog Article</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Topic</label>
                <select
                  value={newCampaign.topic_id}
                  onChange={e => {
                    const topic = approvedTopics.find(t => t.id === e.target.value);
                    setNewCampaign({
                      ...newCampaign,
                      topic_id: e.target.value,
                      name: topic ? topic.title : newCampaign.name,
                    });
                  }}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="">No topic</option>
                  {approvedTopics.map(t => (
                    <option key={t.id} value={t.id}>💡 {t.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Schedule *</label>
                <select
                  value={newCampaign.schedule_type}
                  onChange={e => setNewCampaign({...newCampaign, schedule_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="one_off">One-off</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Due Date & Time *</label>
                <input
                  type="datetime-local"
                  value={newCampaign.due_at}
                  onChange={e => setNewCampaign({...newCampaign, due_at: e.target.value})}
                  className="w-full px-3 py-2 border rounded text-sm"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Variables (JSON, optional)</label>
                <input
                  type="text"
                  value={newCampaign.variable_slots}
                  onChange={e => setNewCampaign({...newCampaign, variable_slots: e.target.value})}
                  placeholder='e.g. {"audience": "commercial", "issue": "8"}'
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
              <div className="col-span-2 flex gap-3">
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700">
                  Create Campaign
                </button>
                <button type="button" onClick={() => setShowNewCampaign(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Main layout: list + detail */}
        <div className="flex gap-6">
          {/* Campaign list */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-3 border-b bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-700">Campaigns</h3>
              </div>

              {loading && (
                <div className="p-6 text-center text-gray-400 text-sm">Loading...</div>
              )}

              {!loading && filtered.length === 0 && (
                <div className="p-6 text-center text-gray-400 text-sm">
                  No {filter === 'all' ? '' : filter} campaigns
                </div>
              )}

              {!loading && filtered.length > 0 && (
                <div className="divide-y max-h-[calc(100vh-280px)] overflow-y-auto">
                  {filtered.map(c => {
                    const sc = STATUS_COLORS[c.status] || STATUS_COLORS.scheduled;
                    const isSelected = selectedId === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => loadDetail(c.id)}
                        className={`w-full text-left p-3 hover:bg-blue-50 transition-colors ${
                          isSelected ? 'bg-blue-50 border-l-2 border-l-blue-600' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm">{TYPE_ICONS[c.type] || '📰'}</span>
                          <span className="text-sm font-medium text-gray-800 truncate flex-1">{c.name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-500">
                          <span className={`${sc.bg} ${sc.text} px-1.5 py-0.5 rounded font-medium`}>{c.status.replace('_', ' ')}</span>
                          <span>{SCHEDULE_ICONS[c.schedule_type] || '📧'} {c.schedule_type?.replace('_', ' ')}</span>
                          <span className="text-gray-400">{CHANNEL_LABELS[c.delivery_channel] || c.delivery_channel}</span>
                        </div>
                        {c.due_at && (
                          <div className="text-[10px] text-gray-400 mt-1">
                            Due: {new Date(c.due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Detail panel */}
          <div className="flex-1 min-w-0">
            {!selectedDetail && !detailLoading && !detailError && (
              <div className="bg-white rounded-lg border shadow-sm p-12 text-center text-gray-400">
                <p className="text-4xl mb-3">📧</p>
                <p className="text-sm">Select a campaign to view details</p>
              </div>
            )}

            {detailLoading && (
              <div className="bg-white rounded-lg border shadow-sm p-12 text-center text-gray-400">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-3" />
                <p className="text-sm">Loading campaign...</p>
              </div>
            )}

            {detailError && !detailLoading && (
              <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
                <p className="text-3xl mb-3">⚠️</p>
                <p className="text-sm text-red-600 font-medium">{detailError}</p>
              </div>
            )}

            {selectedDetail && !detailLoading && (
              <div className="bg-white rounded-lg border shadow-sm">
                {/* Header with status + action buttons */}
                <div className="p-4 border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {(STATUS_COLORS[selectedDetail.status] || STATUS_COLORS.scheduled).dot}
                        <h2 className="text-lg font-semibold text-gray-800">{selectedDetail.name}</h2>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className={`${(STATUS_COLORS[selectedDetail.status] || STATUS_COLORS.scheduled).bg} ${(STATUS_COLORS[selectedDetail.status] || STATUS_COLORS.scheduled).text} px-2 py-0.5 rounded font-medium`}>
                          {selectedDetail.status?.replace('_', ' ')}
                        </span>
                        <span>{TYPE_ICONS[selectedDetail.type] || '📰'} {TYPE_LABELS[selectedDetail.type] || selectedDetail.type}</span>
                        <span>{CHANNEL_LABELS[selectedDetail.delivery_channel] || selectedDetail.delivery_channel}</span>
                        {campaignTemplateLabel(selectedDetail) && (
                          <span>📄 {campaignTemplateLabel(selectedDetail)}</span>
                        )}
                        <span>{SCHEDULE_ICONS[selectedDetail.schedule_type] || '📧'} {selectedDetail.schedule_type?.replace('_', ' ')}</span>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-1">{STATUS_FLOW[selectedDetail.status] || ''}</p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      {canGenerateCampaign(selectedDetail) && (
                        <button
                          onClick={async () => {
                            setActionLoading('generate');
                            try {
                              const res = await apiFetch(`/api/marketing/campaigns/${selectedDetail.id}/generate`, { method: 'POST' });
                              const data = await res.json();
                              if (!res.ok) { alert(`Generate failed: ${data.error || 'Unknown'}`); return; }
                              loadDetail(selectedDetail.id);
                              loadCampaigns();
                            } catch (err) { alert(`Generate failed: ${err.message}`); }
                            finally { setActionLoading(null); }
                          }}
                          disabled={actionLoading === 'generate'}
                          className="px-4 py-2 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                        >
                          {actionLoading === 'generate' ? 'Generating...' : '🤖 Generate Content'}
                        </button>
                      )}
                      {selectedDetail.status === 'awaiting_approval' && (
                        <>
                          <button
                            onClick={async () => {
                              setActionLoading('generate');
                              try {
                                const res = await apiFetch(`/api/marketing/campaigns/${selectedDetail.id}/generate`, { method: 'POST' });
                                const data = await res.json();
                                if (!res.ok) { alert(`Re-generate failed: ${data.error || 'Unknown'}`); return; }
                                loadDetail(selectedDetail.id);
                                loadCampaigns();
                              } catch (err) { alert(`Re-generate failed: ${err.message}`); }
                              finally { setActionLoading(null); }
                            }}
                            disabled={actionLoading === 'generate'}
                            className="px-3 py-2 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 disabled:opacity-50"
                            title="Re-generate content with AI"
                          >
                            {actionLoading === 'generate' ? 'Working...' : '🔄 Re-generate'}
                          </button>
                          <button
                            onClick={() => handleAction('approve', selectedDetail.id)}
                            disabled={actionLoading === 'approve'}
                            className="px-4 py-2 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                          >
                            {actionLoading === 'approve' ? 'Approving...' : '✅ Approve'}
                          </button>
                          <button
                            onClick={() => {
                              const reason = prompt('Reason for rejection (optional):');
                              handleAction('reject', selectedDetail.id, { reason: reason || 'Rejected by user' });
                            }}
                            disabled={actionLoading === 'reject'}
                            className="px-4 py-2 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                          >
                            {actionLoading === 'reject' ? 'Rejecting...' : '❌ Reject'}
                          </button>
                        </>
                      )}
                      {selectedDetail.status === 'approved' && (
                        <button
                          onClick={() => handleAction('send', selectedDetail.id)}
                          disabled={actionLoading === 'send'}
                          className="px-4 py-2 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                        >
                          {actionLoading === 'send' ? 'Sending...' : '📤 Send Now'}
                        </button>
                      )}
                      {selectedDetail.status === 'rejected' && (
                        <button
                          onClick={async () => {
                            setActionLoading('generate');
                            try {
                              // Re-generate content instead of just resetting status
                              const res = await apiFetch(`/api/marketing/campaigns/${selectedDetail.id}/generate`, { method: 'POST' });
                              const data = await res.json();
                              if (!res.ok) { alert(`Re-generate failed: ${data.error || 'Unknown'}`); return; }
                              loadDetail(selectedDetail.id);
                              loadCampaigns();
                            } catch (err) { alert(`Re-generate failed: ${err.message}`); }
                            finally { setActionLoading(null); }
                          }}
                          disabled={actionLoading === 'generate'}
                          className="px-4 py-2 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                        >
                          {actionLoading === 'generate' ? 'Re-generating...' : '🔄 Re-generate'}
                        </button>
                      )}
                      {canDeleteCampaign(selectedDetail) && (
                        <button
                          onClick={() => deleteCampaign(selectedDetail)}
                          disabled={actionLoading === 'delete'}
                          className="px-3 py-2 bg-white text-red-700 border border-red-300 rounded text-xs font-medium hover:bg-red-50 disabled:opacity-50"
                          title="Delete this unused campaign"
                        >
                          {actionLoading === 'delete' ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-medium mb-0.5">Due</p>
                      <p className="text-gray-800">{selectedDetail.due_at ? new Date(selectedDetail.due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
                    </div>
                    {selectedDetail.last_run_at && (
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-medium mb-0.5">Last Generated</p>
                        <p className="text-gray-800">{new Date(selectedDetail.last_run_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    )}
                    {selectedDetail.next_run_at && (
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-medium mb-0.5">Next Run</p>
                        <p className="text-gray-800">{new Date(selectedDetail.next_run_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    )}
                    {selectedDetail.failure_reason && (
                      <div>
                        <p className="text-[10px] text-red-500 uppercase font-medium mb-0.5">Reason</p>
                        <p className="text-red-700 text-xs">{selectedDetail.failure_reason}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Variable Slots */}
                {selectedDetail.variable_slots && Object.keys(selectedDetail.variable_slots).length > 0 && (
                  <div className="p-4 border-b">
                    <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2">Variables</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedDetail.variable_slots).map(([key, val]) => (
                        <div key={key} className="bg-gray-50 rounded px-2 py-1.5">
                          <p className="text-[10px] text-gray-500">{key}</p>
                          <p className="text-xs text-gray-800 truncate">{String(val)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Newsletter preview */}
                {campaignPreviewHtml(selectedDetail) && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">{TYPE_LABELS[selectedDetail.type] || 'Newsletter'} Preview</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowPreview(!showPreview)}
                          className={`text-xs px-3 py-1 rounded border font-medium transition-colors ${
                            showPreview ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300'
                          }`}
                        >
                          {showPreview ? 'Hide Preview' : 'Show Preview'}
                        </button>
                        <button
                          onClick={() => {
                            const w = window.open('', '_blank');
                            w.document.write(campaignPreviewHtml(selectedDetail));
                            w.document.close();
                          }}
                          className="text-xs px-3 py-1 rounded border bg-white text-gray-600 border-gray-300 hover:border-blue-300 font-medium"
                        >
                          Open Full Page
                        </button>
                      </div>
                    </div>


                    {showPreview && (
                      <div className="border rounded-lg overflow-hidden bg-gray-100">
                        <iframe
                          srcDoc={campaignPreviewHtml(selectedDetail)}
                          title="Newsletter Preview"
                          className="w-full border-0"
                          style={{ height: '600px' }}
                          sandbox="allow-same-origin"
                        />
                      </div>
                    )}

                    {selectedDetail.generated_content && (
                      <details className="mt-3">
                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Plain text version</summary>
                        <pre className="mt-2 p-3 bg-gray-50 rounded text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-60 overflow-y-auto">
                          {selectedDetail.generated_content}
                        </pre>
                      </details>
                    )}
                  </div>
                )}

                {/* Template preview (if no generated content yet) */}
                {!campaignPreviewHtml(selectedDetail) && selectedDetail.master_template_key && (
                  <div className="p-4">
                    <div className="rounded border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                      Locked master selected: {campaignTemplateLabel(selectedDetail)}. Content and generated image will appear here after generation.
                    </div>
                  </div>
                )}


                {/* Analytics (for sent/approved campaigns) */}
                {(selectedDetail.status === 'sent' || selectedDetail.status === 'approved') && analytics && (
                  <div className="p-4 border-b">
                    <h3 className="text-xs font-semibold text-gray-600 uppercase mb-3">📊 Analytics</h3>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                      {[
                        { label: 'Sent', value: analytics.stats.sent, icon: '📤' },
                        { label: 'Delivered', value: analytics.stats.delivered, icon: '✅' },
                        { label: 'Opens', value: analytics.stats.opens, icon: '👁️' },
                        { label: 'Clicks', value: analytics.stats.clicks, icon: '👆' },
                        { label: 'Bounces', value: analytics.stats.bounces, icon: '↩️' },
                        { label: 'Unsubs', value: analytics.stats.unsubscribes, icon: '🚫' },
                      ].map(stat => (
                        <div key={stat.label} className="bg-gray-50 rounded p-2 text-center">
                          <p className="text-lg">{stat.icon}</p>
                          <p className="text-lg font-semibold text-gray-800">{stat.value}</p>
                          <p className="text-[10px] text-gray-500">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                    {analytics.stats.sent > 0 && (
                      <div className="mt-2 text-xs text-gray-400">
                        Open rate: {((analytics.stats.opens / analytics.stats.sent) * 100).toFixed(1)}% ·
                        Click rate: {((analytics.stats.clicks / analytics.stats.sent) * 100).toFixed(1)}%
                      </div>
                    )}
                    {analytics.recent_events && analytics.recent_events.length > 0 && (
                      <details className="mt-3">
                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Recent activity</summary>
                        <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                          {analytics.recent_events.map((evt, i) => (
                            <div key={i} className="flex items-center gap-2 text-[11px] text-gray-600">
                              <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                              <span className="font-medium">{evt.event_type}</span>
                              <span className="text-gray-400 truncate">{evt.email}</span>
                              <span className="text-gray-300 ml-auto">{new Date(evt.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}

                {/* Empty state */}
                {!campaignPreviewHtml(selectedDetail) && !selectedDetail.master_template_key && (
                  <div className="p-8 text-center text-gray-400">
                    <p className="text-sm">No newsletter content yet</p>
                    <p className="text-xs mt-1">Content will appear here once Hermes generates it</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CampaignPage;
